/**
 * KPIs de una sesión calculados en cliente a partir del snapshot
 * (GET /api/session/:id/state) y, para la encuesta, de sus respuestas
 * (GET /api/survey/session/:id/responses). Lo usan SessionDetailPage y DashboardPage.
 */

export const ROUNDS = [1, 2, 3];
/** Ronda "0" del snapshot = votación de apertura (solo dato clave). */
export const OPENING_ROUND = 0;

/* ---------- Fases ---------- */

const STEP_LABELS = {
	intro: "Presentación del eje",
	dato: "Dato clave",
	revelado: "Dato clave · respuesta",
	debate: "Debate y respuesta",
	comun: "Puesta en común"
};

const OPENING_LABELS = {
	dato: "Apertura · votación inicial",
	revelado: "Apertura · respuesta y tabla"
};

const CLOSING_LABELS = {
	resumen: "Cierre · resumen"
};

export function phaseLabel(phase) {
	if (!phase) return "—";
	if (phase === "apertura") return "Apertura";
	if (phase === "fin") return "Fin · encuesta";
	const [block, step] = phase.split(".");
	if (block === "apertura") return OPENING_LABELS[step] || `Apertura · ${step}`;
	if (block === "cierre") return CLOSING_LABELS[step] || `Cierre · ${step}`;
	if (/^r\d$/.test(block))
		return `Eje ${block.slice(1)} · ${STEP_LABELS[step] || step}`;
	return phase;
}

/* ---------- Helpers ---------- */

export function roundContentOf(content, n) {
	const list = (content && content.rounds) || [];
	return list.find(r => r.index === n) || list[n - 1] || {};
}

export function tableNameOf(tables, id) {
	const t = (tables || []).find(x => String(x._id) === String(id));
	if (!t) return "Mesa desconocida";
	return t.name || `Mesa ${t.slot}`;
}

export function pct(part, total) {
	return total ? Math.round((part / total) * 100) : 0;
}

/** Estadística de un dato clave (apertura o eje) a partir de su contenido y de rounds[n].surprise. */
export function surpriseStats(surprise, data) {
	const sp = surprise || {};
	const d = data || {};
	const options = sp.options || [];
	const counts = options.map((_, i) => (d.counts && d.counts[i]) || 0);
	const total = d.total != null ? d.total : counts.reduce((a, b) => a + b, 0);
	const correctIndex =
		typeof sp.correctIndex === "number" ? sp.correctIndex : null;
	const correctCount = correctIndex != null ? counts[correctIndex] || 0 : 0;
	return {
		question: sp.question || "",
		fact: sp.fact || "",
		source: sp.source || "",
		options,
		counts,
		total,
		correctIndex,
		correctCount,
		correctPct: pct(correctCount, total),
		answered: d.answered || [],
		rows: options.map((option, i) => ({
			option,
			count: counts[i],
			pct: pct(counts[i], total),
			correct: i === correctIndex
		}))
	};
}

/* ---------- KPIs del snapshot ---------- */

export function computeKpis(snapshot) {
	const session = (snapshot && snapshot.session) || {};
	const content = (snapshot && snapshot.content) || {};
	const tables = (snapshot && snapshot.tables) || [];
	const rounds = (snapshot && snapshot.rounds) || {};

	const registered = tables.length;
	const connected = tables.filter(t => t.connected).length;
	const simulated = tables.filter(t => t.simulated).length;
	const planned = session.numTables || 0;

	const roundKpis = ROUNDS.map(n => {
		const rc = roundContentOf(content, n);
		const responses = (rounds[n] && rounds[n].responses) || [];
		return {
			index: n,
			title: rc.title || `Eje ${n}`,
			shortTitle: rc.shortTitle || `Eje ${n}`,
			question: rc.question || "",
			responses: responses.length,
			responsesPct: pct(responses.length, registered),
			revealed: !!(rounds[n] && rounds[n].revealed),
			surprise: surpriseStats(rc.surprise, rounds[n] && rounds[n].surprise)
		};
	});

	const totalResponses = roundKpis.reduce((acc, r) => acc + r.responses, 0);

	return {
		session,
		phase: {
			value: session.phase || "",
			label: phaseLabel(session.phase),
			index: (session.phaseIndex != null ? session.phaseIndex : -1) + 1,
			total: (session.phases || []).length,
			startedAt: session.phaseStartedAt || null,
			eyeProgress: session.eyeProgress || 0
		},
		tables: { registered, connected, planned, simulated },
		opening: {
			revealed: !!(rounds[OPENING_ROUND] && rounds[OPENING_ROUND].revealed),
			surprise: surpriseStats(
				content.opening && content.opening.surprise,
				rounds[OPENING_ROUND] && rounds[OPENING_ROUND].surprise
			)
		},
		rounds: roundKpis,
		totalResponses,
		expectations: {
			question: (content.expectations && content.expectations.question) || "",
			total: (snapshot && snapshot.expectations && snapshot.expectations.responses) || 0
		},
		survey: {
			total: (snapshot && snapshot.survey && snapshot.survey.responses) || 0,
			questions: (content.survey && content.survey.questions) || []
		}
	};
}

/* ---------- Encuesta ---------- */

/** Etiqueta de una pregunta de encuesta: su texto o, si va vacío, el título del grupo. */
export function questionLabel(q) {
	return (q && (q.text || q.group || q.key)) || "";
}

function isAnswered(v) {
	return v !== undefined && v !== null && String(v).trim() !== "";
}

/**
 * Resumen de la encuesta: media por pregunta de escala, reparto de las de opción
 * y nº de respuestas con texto en las libres. `responses` = [{ answers: {key: valor} }].
 */
export function surveyStats(questions, responses) {
	const qs = questions || [];
	const rs = responses || [];
	let sum = 0;
	let n = 0;

	const scale = qs
		.filter(q => q.type === "scale")
		.map(q => {
			const max = q.max || 5;
			const min = q.min || 1;
			const vals = rs
				.map(r => Number(r.answers && r.answers[q.key]))
				.filter(v => !Number.isNaN(v) && v >= min && v <= max);
			const dist = Array.from({ length: max - min + 1 }, (_, i) => ({
				value: min + i,
				count: vals.filter(v => v === min + i).length
			}));
			const s = vals.reduce((a, b) => a + b, 0);
			sum += s;
			n += vals.length;
			return {
				key: q.key,
				label: questionLabel(q),
				n: vals.length,
				avg: vals.length ? Math.round((s / vals.length) * 100) / 100 : null,
				min,
				max,
				dist
			};
		});

	const choice = qs
		.filter(q => q.type === "choice")
		.map(q => {
			const answers = rs
				.map(r => r.answers && r.answers[q.key])
				.filter(isAnswered)
				.map(v => String(v));
			const options = (q.options || []).slice();
			answers.forEach(a => {
				if (!options.includes(a)) options.push(a);
			});
			return {
				key: q.key,
				label: questionLabel(q),
				n: answers.length,
				options: options.map(option => {
					const count = answers.filter(a => a === option).length;
					return { option, count, pct: pct(count, answers.length) };
				})
			};
		});

	const text = qs
		.filter(q => q.type === "text")
		.map(q => ({
			key: q.key,
			label: questionLabel(q),
			n: rs.filter(r => isAnswered(r.answers && r.answers[q.key])).length
		}));

	return {
		total: rs.length,
		scale,
		choice,
		text,
		globalAvg: n ? Math.round((sum / n) * 100) / 100 : null,
		globalN: n,
		globalMax: scale.length ? Math.max(...scale.map(s => s.max)) : 5
	};
}
